import { useEffect, useState } from "react";
import { Plus, Trash2, Mail, Phone, User, Star } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";

/**
 * ContactsEditor — inline rows editor for a customer's contacts array.
 * Used both inside CustomerForm (create/edit) and inside a Dialog on the
 * Customer detail Contacts tab.
 *
 * Props:
 *   value: Array<Contact>
 *   onChange: (contacts: Contact[]) => void
 *   testIdPrefix: string  (e.g. "customer-form-contacts", "customer-contacts")
 */
export default function ContactsEditor({ value, onChange, testIdPrefix = "contacts" }) {
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);

  useEffect(() => {
    api.get("/settings/contact-roles", { params: { active: "true" } })
      .then(({ data }) => setRoles(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setRolesLoading(false));
  }, []);

  const addRow = () => {
    onChange([
      ...(value || []),
      {
        id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name: "",
        email: "",
        phone: "",
        role: roles[0]?.name || "Primary",
        is_report_recipient: (value || []).length === 0,
        notes: "",
      },
    ]);
  };

  const update = (idx, patch) => {
    const next = (value || []).map((c, i) => (i === idx ? { ...c, ...patch } : c));
    onChange(next);
  };

  const remove = (idx) => {
    const next = (value || []).filter((_, i) => i !== idx);
    onChange(next);
  };

  return (
    <Card className="rounded-md" data-testid={`${testIdPrefix}-card`}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <User className="h-4 w-4 text-primary" /> Contacts
        </CardTitle>
        <Button type="button" size="sm" variant="outline" onClick={addRow} disabled={rolesLoading} data-testid={`${testIdPrefix}-add-btn`}>
          <Plus className="h-3.5 w-3.5" /> Add contact
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {(value || []).length === 0 && (
          <div className="text-xs text-muted-foreground italic" data-testid={`${testIdPrefix}-empty`}>
            No contacts yet — add at least one so this customer can receive reports.
          </div>
        )}
        {(value || []).map((c, i) => (
          <div key={c.id} className="border border-border rounded-sm p-3 space-y-3" data-testid={`${testIdPrefix}-row-${i}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Name <span className="text-destructive">*</span></Label>
                <Input value={c.name || ""} onChange={(e) => update(i, { name: e.target.value })} data-testid={`${testIdPrefix}-name-${i}`} />
              </div>
              <div className="space-y-1">
                <Label>Role</Label>
                <Select value={c.role || undefined} onValueChange={(v) => update(i, { role: v })}>
                  <SelectTrigger data-testid={`${testIdPrefix}-role-${i}`}><SelectValue placeholder="Role" /></SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => (<SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="flex items-center gap-1"><Mail className="h-3 w-3" /> Email</Label>
                <Input value={c.email || ""} onChange={(e) => update(i, { email: e.target.value })} data-testid={`${testIdPrefix}-email-${i}`} />
              </div>
              <div className="space-y-1">
                <Label className="flex items-center gap-1"><Phone className="h-3 w-3" /> Phone</Label>
                <Input value={c.phone || ""} onChange={(e) => update(i, { phone: e.target.value })} data-testid={`${testIdPrefix}-phone-${i}`} />
              </div>
              <div className="md:col-span-2 space-y-1">
                <Label>Notes</Label>
                <Textarea value={c.notes || ""} onChange={(e) => update(i, { notes: e.target.value })} rows={2} data-testid={`${testIdPrefix}-notes-${i}`} />
              </div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Switch checked={!!c.is_report_recipient} onCheckedChange={(v) => update(i, { is_report_recipient: v })} data-testid={`${testIdPrefix}-report-${i}`} />
                <Label className="text-xs uppercase tracking-wider font-bold flex items-center gap-1">
                  <Star className="h-3 w-3 text-warning" /> Receives PDF reports
                </Label>
                {c.is_report_recipient && <StatusBadge tone="warning">Report</StatusBadge>}
              </div>
              <Button type="button" size="sm" variant="ghost" onClick={() => remove(i)} className="text-destructive" data-testid={`${testIdPrefix}-remove-${i}`}>
                <Trash2 className="h-3.5 w-3.5" /> Remove
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
